<template>
  <div class="flex min-h-0 flex-col overflow-hidden" :style="{ maxHeight: maxHeightStyle }">
    <div v-if="breadcrumbs.length > 0" class="mb-1 border-b border-gray-20 px-1 pb-1">
      <button
        ref="backButtonRef"
        data-testid="multilevel-select-back"
        type="button"
        class="flex w-full items-center gap-2 rounded-sm border-none bg-transparent px-3 py-2 text-left text-sm text-gray-70 outline-none outline-0 transition-colors duration-150 ease-in hover:bg-active-secondary focus:bg-active-secondary"
        @click="goBackWithMouse"
        @keydown.enter.stop.prevent="goBackWithKeyboard"
        @keydown.space.stop.prevent="goBackWithKeyboard"
        @keydown.up.stop.prevent="focusLastOption"
        @keydown.down.stop.prevent="focusFirstOption"
        @keydown.home.stop.prevent="focusFirstOption"
        @keydown.end.stop.prevent="focusLastOption"
        @keydown.esc.stop.prevent="handleEscape"
      >
        <arrow-left-icon class="h-4 w-4" />
        {{ breadcrumbLabel }}
      </button>
    </div>

    <ul
      ref="dropdownRef"
      data-testid="multilevel-select-dropdown"
      class="flex min-h-0 flex-1 list-none flex-col gap-0.5 overflow-y-auto pb-1"
      role="menu"
      tabindex="-1"
      @keydown.up.stop.prevent="focusLastOption"
      @keydown.down.stop.prevent="focusFirstOption"
      @keydown.home.stop.prevent="focusFirstOption"
      @keydown.end.stop.prevent="focusLastOption"
      @keydown.esc.stop.prevent="handleEscape"
    >
      <template v-if="currentOptions.length > 0">
        <ui-menu-item
          v-for="option in currentOptions"
          :key="option.id"
          role="none"
          auto-height
          :truncate-content="false"
          :selected="isSelected(option)"
          :button-attrs="optionButtonAttrs"
          @select="selectOption(option)"
          @keydown.enter.stop.prevent="selectOption(option, focusFirstOption)"
          @keydown.space.stop.prevent="selectOption(option, focusFirstOption)"
          @keydown.up.stop.prevent="focusPreviousOption"
          @keydown.down.stop.prevent="focusNextOption"
          @keydown.home.stop.prevent="focusFirstOption"
          @keydown.end.stop.prevent="focusLastOption"
          @keydown.esc.stop.prevent="handleEscape"
        >
          <span class="flex w-full items-center justify-between gap-2 whitespace-normal py-2">
            <span class="min-w-0 flex-1 break-all font-normal" :class="{ 'font-medium': isSelected(option) }">
              {{ option.name }}
            </span>
            <chevron-right-icon v-if="hasChildren(option)" class="h-4 w-4 shrink-0 text-gray-50" />
          </span>
        </ui-menu-item>
      </template>
      <li v-else class="px-4 py-3 text-sm text-gray-50" role="none">
        {{ emptyLabel }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import { ChevronRightIcon, ArrowLeftIcon } from '@klausapp/ui-kit/icons'
import type { MultiLevelOption } from '../types'
import UiMenuItem from './UiMenu/UiMenuItem.vue'

interface Props {
  modelValue?: string | null
  options: MultiLevelOption[]
  rootLabel?: string
  emptyLabel?: string
  maxHeight?: number | string
}

interface Emits {
  (e: 'update:modelValue', value: string | null): void
  (e: 'select', value: string): void
  (e: 'escape'): void
  (e: 'navigation-change', value: { depth: number; isNested: boolean }): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  rootLabel: '',
  emptyLabel: '',
  maxHeight: undefined,
})

const emit = defineEmits<Emits>()

const breadcrumbs = ref<MultiLevelOption[]>([])
const dropdownRef = ref<HTMLUListElement | null>(null)
const backButtonRef = ref<HTMLButtonElement | null>(null)

const currentOptions = computed(() => breadcrumbs.value.at(-1)?.children ?? props.options)

const maxHeightStyle = computed(() => {
  if (props.maxHeight === undefined) return undefined
  return typeof props.maxHeight === 'number' ? `${props.maxHeight}px` : props.maxHeight
})

const breadcrumbLabel = computed(() => {
  if (breadcrumbs.value.length === 0) return ''
  return breadcrumbs.value.length === 1
    ? props.rootLabel || breadcrumbs.value[0].name
    : breadcrumbs.value.at(-2)?.name || ''
})

const optionButtonAttrs = {
  'data-option': '',
  'data-testid': 'multilevel-select-option',
  role: 'menuitem',
  tabindex: -1,
}

const getOptionElement = (target: EventTarget | null) =>
  target instanceof HTMLElement ? target.closest<HTMLElement>('[data-option]') : null

const getOptionElements = () => Array.from(dropdownRef.value?.querySelectorAll<HTMLElement>('[data-option]') ?? [])

const focusDropdown = () => dropdownRef.value?.focus()
const focusFirstOption = () => getOptionElements().at(0)?.focus()
const focusLastOption = () => getOptionElements().at(-1)?.focus()

const focusOptionByOffset = (currentOption: EventTarget | null, offset: number) => {
  if (!(currentOption instanceof HTMLElement)) return

  const optionElements = getOptionElements()
  const currentIndex = optionElements.indexOf(currentOption)

  if (currentIndex === -1) return

  const nextIndex = currentIndex + offset
  optionElements[nextIndex]?.focus()
}

const focusPreviousOption = (event: KeyboardEvent) => {
  const optionElements = getOptionElements()
  const currentOption = getOptionElement(event.target)

  if (currentOption === optionElements.at(0) && breadcrumbs.value.length > 0) {
    backButtonRef.value?.focus()
    return
  }

  focusOptionByOffset(currentOption, -1)
}

const focusNextOption = (event: KeyboardEvent) => {
  focusOptionByOffset(getOptionElement(event.target), 1)
}

const hasChildren = (option: MultiLevelOption) => !!option.children?.length

const isSelected = (option: MultiLevelOption) => props.modelValue === option.id

const emitNavigationChange = () => {
  emit('navigation-change', {
    depth: breadcrumbs.value.length,
    isNested: breadcrumbs.value.length > 0,
  })
}

const selectOption = async (option: MultiLevelOption, focusAfterDrilldown = focusDropdown) => {
  if (hasChildren(option)) {
    breadcrumbs.value.push(option)
    emitNavigationChange()
    await nextTick()
    focusAfterDrilldown?.()
    return
  }

  emit('update:modelValue', option.id)
  emit('select', option.id)
}

const goBack = async (focusAfterNavigation = focusDropdown) => {
  breadcrumbs.value.pop()
  emitNavigationChange()

  await nextTick()
  focusAfterNavigation()
}

const goBackWithMouse = () => goBack()
const goBackWithKeyboard = () => goBack(focusFirstOption)

const handleEscape = () => {
  emit('escape')
}

const reset = () => {
  breadcrumbs.value = []
  emitNavigationChange()
}

defineExpose({
  focusFirstOption,
  focusLastOption,
  reset,
})
</script>
